import { useState, useEffect, useRef } from "react";
import { useTheme } from "../ThemeContext";
import { Card } from "../ui/card";
import { Badge } from "../ui/badge";
import { MoveRight, TrendingUp, TrendingDown, RefreshCcw, BrainCircuit } from "lucide-react";
import ShinyText from "../reactbits/ShinyText";
import { motion } from "motion/react";
import PhoneMockup from "../PhoneMockup";

import videoA from "../../assets/supp.mp4"; // Fast paced, maybe less relaxing
import videoB from "../../assets/bath.mp4"; // Slower paced, more engaging
import videoC from "../../assets/retreat_full_1.mp4"; // Optimal nature video

export function AdaptiveContent() {
    const { theme } = useTheme();
    const [phase, setPhase] = useState<'testing' | 'learning'>('testing');
    // The cards stack below xl; there the highlight follows the scroll position instead of a timer
    const [stacked, setStacked] = useState(false);
    const testingRef = useRef<HTMLDivElement>(null);
    const learningRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const mq = window.matchMedia("(max-width: 1279px)");
        const update = () => setStacked(mq.matches);
        update();
        mq.addEventListener("change", update);
        return () => mq.removeEventListener("change", update);
    }, []);

    useEffect(() => {
        if (stacked) return;
        const timer = setInterval(() => {
            setPhase(p => p === 'testing' ? 'learning' : 'testing');
        }, 6000);
        return () => clearInterval(timer);
    }, [stacked]);

    useEffect(() => {
        if (!stacked) return;
        const targets: [Element | null, 'testing' | 'learning'][] = [
            [testingRef.current, 'testing'],
            [learningRef.current, 'learning'],
        ];
        // A thin band across the middle of the screen: whichever card crosses it is the one in focus
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const match = targets.find(([el]) => el === entry.target);
                    if (match && entry.isIntersecting) setPhase(match[1]);
                });
            },
            { rootMargin: "-50% 0px -50% 0px" }
        );
        targets.forEach(([el]) => el && observer.observe(el));
        return () => observer.disconnect();
    }, [stacked]);

    return (
        <div className="w-full max-w-7xl mx-auto py-10 md:py-24">
            <div className="mb-6 md:mb-14 text-center max-w-3xl mx-auto">
                <Badge variant="outline" className="mb-3 md:mb-4 px-4 py-2 bg-indigo-500/10 border-indigo-500/30">
                    <BrainCircuit className="w-4 h-4 mr-2 text-indigo-500" />
                    <ShinyText text="Continuous Self-Learning" speed={3} className="font-medium" />
                </Badge>
                <h3 className={`text-3xl md:text-5xl font-bold mb-3 md:mb-6 ${theme === "dark" ? "text-white" : "text-slate-900"}`}>
                    Evolution Through Engagement
                </h3>
                <p className={`text-base md:text-lg ${theme === "dark" ? "text-slate-400" : "text-slate-600"}`}>
                    Quilt never stops learning. By running parallel micro-tests on therapeutic content,
                    the engine autonomously identifies the optimal content features for each individual patient.
                </p>
            </div>

            <div className="flex flex-col xl:flex-row items-stretch gap-4 md:gap-8 relative">
                
                {/* A/B Testing Phase */}
                <Card ref={testingRef} className={`flex-1 p-4 md:p-8 flex flex-col items-center border shadow-xl relative overflow-hidden transition-all duration-700 ${theme === "dark" ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200"} ${phase === 'testing' ? 'ring-2 ring-indigo-500/50' : 'opacity-70 scale-95'}`}>
                    <div className="text-center w-full mb-4 md:mb-8">
                        <h4 className={`text-xl font-bold mb-2 ${theme === "dark" ? "text-slate-200" : "text-slate-800"}`}>Phase 1: Adaptive Learning</h4>
                        <p className={`text-sm ${theme === "dark" ? "text-slate-400" : "text-slate-500"}`}>Comparing engagement drops across visual styles</p>
                    </div>

                    <div className="flex flex-row items-start sm:items-center justify-center gap-3 sm:gap-6 w-full max-w-2xl">
                        
                        {/* Variant A */}
                        <div className="flex flex-col items-center gap-2 sm:gap-4 w-1/2 min-w-0">
                            <div className="relative w-full max-w-[180px] rounded-2xl overflow-hidden border-2 sm:border-4 border-slate-800 aspect-[9/16] shadow-lg">
                                <video src={videoA} autoPlay loop muted playsInline className="absolute inset-0 w-full h-full object-cover" />
                                <div className="absolute top-2 left-2 bg-black/60 backdrop-blur-sm text-white text-[11px] uppercase font-bold px-2 py-1 rounded">
                                    Variant A
                                </div>
                                <div className="absolute bottom-2 left-2 right-2 flex flex-wrap gap-1 z-10">
                                    <span className="bg-black/60 backdrop-blur-sm text-white text-[11px] font-medium px-1.5 py-0.5 rounded">#high_velocity</span>
                                    <span className="bg-black/60 backdrop-blur-sm text-white text-[11px] font-medium px-1.5 py-0.5 rounded">#bright_colors</span>
                                </div>
                            </div>
                            <div className={`p-2.5 sm:p-4 rounded-xl w-full border ${theme === "dark" ? "bg-slate-800/50 border-slate-700" : "bg-slate-50 border-slate-200"}`}>
                                <div className="flex justify-between items-center mb-2">
                                    <span className={`text-xs font-semibold ${theme === "dark" ? "text-slate-300" : "text-slate-700"}`}>Watch Time</span>
                                    <TrendingDown className="w-4 h-4 text-red-500" />
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className={`text-xs font-semibold ${theme === "dark" ? "text-slate-300" : "text-slate-700"}`}>Completion Rate</span>
                                    <TrendingDown className="w-4 h-4 text-red-500" />
                                </div>
                            </div>
                        </div>

                        <div className={`hidden sm:flex flex-shrink-0 items-center justify-center w-10 h-10 rounded-full font-bold ${theme === "dark" ? "bg-slate-800 text-slate-400" : "bg-slate-100 text-slate-500"}`}>
                            VS
                        </div>

                        {/* Variant B */}
                        <div className="flex flex-col items-center gap-2 sm:gap-4 w-1/2 min-w-0">
                            <div className="relative w-full max-w-[180px] rounded-2xl overflow-hidden border-2 sm:border-4 border-slate-800 aspect-[9/16] shadow-lg">
                                <video src={videoB} autoPlay loop muted playsInline className="absolute inset-0 w-full h-full object-cover" />
                                <div className="absolute top-2 left-2 bg-black/60 backdrop-blur-sm text-white text-[11px] uppercase font-bold px-2 py-1 rounded">
                                    Variant B
                                </div>
                                <div className="absolute bottom-2 left-2 right-2 flex flex-wrap gap-1 z-10">
                                    <span className="bg-black/60 backdrop-blur-sm text-white text-[11px] font-medium px-1.5 py-0.5 rounded">#slow_pacing</span>
                                    <span className="bg-black/60 backdrop-blur-sm text-white text-[11px] font-medium px-1.5 py-0.5 rounded">#nature_themed</span>
                                </div>
                            </div>
                            <div className={`p-2.5 sm:p-4 rounded-xl w-full border ${theme === "dark" ? "bg-slate-800/50 border-indigo-500/30 shadow-[0_0_15px_rgba(99,102,241,0.1)]" : "bg-indigo-50 border-indigo-200 shadow-sm"}`}>
                                <div className="flex justify-between items-center mb-2">
                                    <span className={`text-xs font-semibold ${theme === "dark" ? "text-slate-300" : "text-slate-700"}`}>Watch Time</span>
                                    <TrendingUp className="w-4 h-4 text-green-500" />
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className={`text-xs font-semibold ${theme === "dark" ? "text-slate-300" : "text-slate-700"}`}>Completion Rate</span>
                                    <TrendingUp className="w-4 h-4 text-green-500" />
                                </div>
                            </div>
                        </div>

                    </div>
                </Card>

                {/* Flow Arrow */}
                <div className="hidden xl:flex items-center justify-center flex-shrink-0 z-10 -mx-6">
                    <div className={`p-4 rounded-full border shadow-xl ${theme === "dark" ? "bg-slate-800 border-slate-700 text-indigo-400" : "bg-white border-slate-200 text-indigo-600"}`}>
                        <MoveRight className="w-8 h-8" />
                    </div>
                </div>

                {/* Adaptive Output Phase */}
                <Card ref={learningRef} className={`flex-1 p-4 md:p-8 flex flex-col items-center border shadow-xl relative overflow-hidden transition-all duration-700 ${theme === "dark" ? "bg-indigo-950/20 border-indigo-500/30" : "bg-indigo-50/50 border-indigo-200"} ${phase === 'learning' ? 'ring-2 ring-indigo-500/50' : 'opacity-70 scale-95'}`}>
                    
                    <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 rounded-full blur-3xl -mx-20 -my-20 pointer-events-none" />

                    <div className="text-center w-full mb-4 md:mb-8 relative z-10">
                        <div className="inline-flex items-center gap-2 mb-2">
                            <h4 className={`text-xl font-bold ${theme === "dark" ? "text-indigo-300" : "text-indigo-800"}`}>Phase 2: Optimized Content</h4>
                            <RefreshCcw className={`w-4 h-4 animate-spin-slow ${theme === "dark" ? "text-indigo-400" : "text-indigo-600"}`} />
                        </div>
                        <p className={`text-sm ${theme === "dark" ? "text-slate-400" : "text-slate-600"}`}>Synthesizing attributes from winning variants</p>
                    </div>

                    <div className="grid grid-cols-[6.5rem_1fr] items-start gap-3 sm:flex sm:flex-row sm:items-center sm:justify-center sm:gap-8 w-full relative z-10">
                        
                        <div className="sm:hidden relative aspect-[9/16] w-full overflow-hidden rounded-2xl border-2 border-slate-800 shadow-lg">
                            <video src={videoC} autoPlay loop muted playsInline className="absolute inset-0 h-full w-full object-cover" />
                        </div>

                        <div className="hidden sm:flex flex-col items-center">
                            <PhoneMockup className="scale-[0.7] transform origin-top mx-auto -mb-[150px]">
                                <video src={videoC} autoPlay loop muted playsInline className="absolute inset-0 w-full h-full object-cover" />
                            </PhoneMockup>
                        </div>

                        <div className="contents sm:flex sm:flex-col sm:gap-4 sm:w-full sm:max-w-sm">
                            
                            <div className={`p-3 sm:p-4 self-stretch rounded-xl border ${theme === "dark" ? "bg-black/40 border-slate-800" : "bg-white border-slate-200"}`}>
                                <h5 className={`text-xs font-bold uppercase tracking-wider mb-3 ${theme === "dark" ? "text-indigo-400" : "text-indigo-600"}`}>Learned Preferences</h5>
                                <ul className={`space-y-2 sm:space-y-3 text-xs sm:text-sm ${theme === "dark" ? "text-slate-300" : "text-slate-700"}`}>
                                    <li className="flex items-start gap-2">
                                        <TrendingUp className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
                                        <span>Lower visual velocity (+45% retention)</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <TrendingUp className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
                                        <span>Nature-themed elements (+20% completion)</span>
                                    </li>
                                </ul>
                            </div>

                            <div className={`col-span-2 sm:col-span-1 p-3 sm:p-4 rounded-xl border font-mono text-xs overflow-hidden ${theme === "dark" ? "bg-slate-900 border-slate-800 text-slate-300" : "bg-slate-100 border-slate-200 text-slate-700"}`}>
                                <div className="text-[11px] text-slate-500 mb-2 uppercase select-none">Generation Prompt Fragment</div>
                                <code className="block whitespace-pre-wrap leading-relaxed text-indigo-600 dark:text-indigo-400">
                                    {"{"}
                                    <br />
                                    &nbsp;&nbsp;"style": "nature_calm_v2",
                                    <br />
                                    &nbsp;&nbsp;"pacing": "slow_ambient",
                                    <br />
                                    &nbsp;&nbsp;"visual_complexity": "low",
                                    <br />
                                    &nbsp;&nbsp;"color_palette": "earth_tones"
                                    <br />
                                    {"}"}
                                </code>
                            </div>

                        </div>
                    </div>
                </Card>
            </div>
        </div>
    );
}

export default AdaptiveContent;
